// The mail markup for one link placement (email spec §7.2, §8.1): the
// first question as linked images, with a placeholder where each link's
// invite token goes. The preview is the same HTML in a sandboxed frame with
// scripts off, which is how a mail client renders it.

import { Button, CopyButton, Dialog, Input } from "@pej/kore/components";
import { useId, useState } from "react";
import { useEmailBlock, withPlaceholder } from "@/api/invites";

export const DEFAULT_PLACEHOLDER = "{token}";

export function EmailBlockDialog({
    name,
    placementId,
    open,
    onOpenChange,
}: {
    name: string;
    placementId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const [placeholder, setPlaceholder] = useState(DEFAULT_PLACEHOLDER);
    const effective = placeholder.trim() === "" ? DEFAULT_PLACEHOLDER : placeholder;
    const fetched = useEmailBlock(placementId, open);
    const block = {
        data: fetched.data === undefined ? undefined : withPlaceholder(fetched.data, effective),
        isError: fetched.isError,
    };
    const placeholderId = useId();
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <Dialog.Content size="xl">
                <Dialog.Header>
                    <Dialog.Title>Email block for {name}</Dialog.Title>
                    <Dialog.Description>
                        Paste this into your mail template. The placeholder stands where each recipient's invite token
                        goes; set it to your mail tool's merge syntax.
                    </Dialog.Description>
                </Dialog.Header>
                <div className="flex min-w-0 flex-col gap-4">
                    <div className="flex flex-col gap-1">
                        <label htmlFor={placeholderId} className="text-sm font-medium">
                            Token placeholder
                        </label>
                        <Input
                            id={placeholderId}
                            value={placeholder}
                            onChange={(event) => setPlaceholder(event.target.value)}
                            placeholder={DEFAULT_PLACEHOLDER}
                        />
                    </div>
                    <div className="flex min-w-0 flex-col gap-1">
                        <p className="text-sm font-medium">Preview</p>
                        {block.data ? (
                            <iframe
                                title={`Email block preview for ${name}`}
                                sandbox=""
                                srcDoc={`<!doctype html><body style="margin:16px;background:#ffffff">${block.data}</body>`}
                                className="h-56 w-full rounded-kore-md border border-kore-border-soft bg-white"
                            />
                        ) : (
                            <p className="text-sm text-kore-text-muted">
                                {block.isError ? "Couldn't load the email block." : "Loading…"}
                            </p>
                        )}
                    </div>
                    <div className="flex min-w-0 flex-col gap-1">
                        <p className="text-sm font-medium">HTML</p>
                        <div className="flex items-start gap-2">
                            <pre className="max-h-48 min-w-0 flex-1 overflow-auto rounded-kore-md border border-kore-border-soft bg-kore-bg p-3 text-xs whitespace-pre-wrap">
                                {block.data ?? ""}
                            </pre>
                            <CopyButton value={block.data ?? ""} label="Copy email block" />
                        </div>
                    </div>
                </div>
                <Dialog.Footer>
                    <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                        Close
                    </Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
}
